import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function LaneConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);

    // Keep this list stable across renders to avoid unnecessary child rerenders
    const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

    // Centralize route to avoid duplication/typos
    const CRUD_ROUTE = '/home/lanes/laneConfigCrud';

    //===========================================================================================================
    // openLaneCrud:
    // - Stable (useCallback) handler to open/focus the Lane CRUD tab.
    // - Persists 'operation' in global state; for View/Edit/Copy also persists the selected row.
    // - Validates presence of a row for operations that require it; shows a localized toast on failure.
    // - Builds the tab label using i18n and the lane's "Source -> Destination".
    // - Passing a stable callback prevents MTable refetches triggered by changing prop identities.
    //===========================================================================================================
    const openLaneCrud = useCallback(
        (operation, row) => {
            // Persist operation for the CRUD screen
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            // Ensure row data is present for operations that need it
            if (['Edit', 'Copy', 'View'].includes(operation)) {
                if (!row) { // If not row/entity data for the provided operation, show error toast and abort
                    toastMessages('error', '', t('SelectedLine'));
                    return;
                }
                dispatch(M_StateActions.addM_State({ key: 'LaneLine', value: row }));
            }

            // Compute the tab label based on the chosen operation
            const tabLabel =
                operation === 'Create'
                    ? t('LabelNewLane')
                    : operation === 'View'
                    ? `${t('LabelViewLane')} "${row?.Source ?? ''} → ${row?.Destination ?? ''}"`
                    : `${t('LabelEditLane')} "${row?.Source ?? ''} → ${row?.Destination ?? ''}"`;

            onCreateTab(CRUD_ROUTE, tabLabel); // Open/focus CRUD tab
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    //===========================================================================================================
    // replaceFunctions:
    // - Memoized map of context actions consumed by MTable.
    // - Using useMemo ensures the Map identity is stable across renders,
    //      preventing unnecessary re-renders and duplicate fetches in MTable.
    // ===========================================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openLaneCrud('View', row)],
                ['Create', () => openLaneCrud('Create')],
                ['Edit', (row) => openLaneCrud('Edit', row)],
            ]),
        [openLaneCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div className="titleSettings" style={{ color: ctx.secondaryColor }}>{t('Lanes')}</div>
                    <div className='labelSubtitleSettings'>{t("InfoLanes")}</div>
                </div>
            </div>
            <MTable // Dynamic Table for Lanes
                filtersOnTable // Enable filters on the table
                entityName={'Lanes'}
                roleName={'Locations'}
                rolePermissions={roles?.map(vl => vl.Value)} // User roles for permission management in the Locations Module
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // replaceFunctions for CRUD operations
            />
        </div>
    );
}

LaneConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
